export type PoliticaVencimientoTipo =
  | 'DIA_PERIODO'
  | 'DIA_SEMANA_MES'
  | 'DIA_VENCIMIENTO_BASE'
  | 'VENCIMIENTO_MENOS_N'
  | 'VENCIMIENTO_MAS_N';

export type AjusteDiaNoHabil = 'NINGUNO' | 'DIA_HABIL_ANTERIOR' | 'DIA_HABIL_SIGUIENTE';

export interface CategoriaActividadPoliticaVencimientoDTO {
  categoriaActividadId?: number;
  aplicar?: boolean;
  nombre: string;
  descripcion?: string | null;
  tipo: PoliticaVencimientoTipo;
  ajusteDiaNoHabil?: AjusteDiaNoHabil | null;
  diaPeriodo?: number | null;
  diaSemana?: number | null;
  ocurrencia?: number | null;
  dias?: number | null;
  created?: string;
  createdBy?: number;
  updated?: string;
  updatedBy?: number;
}

export const POLITICA_VENCIMIENTO_API = (categoriaActividadId: string | number) =>
  `api/categoria-actividads/${categoriaActividadId}/politica-vencimiento`;

export const TIPO_POLITICA_OPTIONS = [
  { value: 'DIA_VENCIMIENTO_BASE', label: 'Vencimiento SAT (con desplazamiento)' },
  { value: 'DIA_PERIODO', label: 'Día fijo del mes del periodo' },
  { value: 'DIA_SEMANA_MES', label: 'N-ésimo día de la semana del mes' },
  { value: 'VENCIMIENTO_MENOS_N', label: 'N días antes del vencimiento SAT (legacy)' },
  { value: 'VENCIMIENTO_MAS_N', label: 'N días después del vencimiento SAT (legacy)' },
] as const;

export const AJUSTE_DIA_NO_HABIL_OPTIONS = [
  { value: 'NINGUNO', label: 'Sin ajuste' },
  { value: 'DIA_HABIL_ANTERIOR', label: 'Día hábil anterior' },
  { value: 'DIA_HABIL_SIGUIENTE', label: 'Día hábil siguiente' },
] as const;

export const DIA_SEMANA_OPTIONS = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miércoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sábado' },
  { value: '7', label: 'Domingo' },
] as const;

export const OCURRENCIA_OPTIONS = [
  { value: '1', label: 'Primero' },
  { value: '2', label: 'Segundo' },
  { value: '3', label: 'Tercero' },
  { value: '4', label: 'Cuarto' },
  { value: '5', label: 'Quinto' },
] as const;

export function isDiaVencimientoBase(tipo: PoliticaVencimientoTipo): boolean {
  return tipo === 'DIA_VENCIMIENTO_BASE';
}

export function isTipoLegacyRelativo(tipo: PoliticaVencimientoTipo): boolean {
  return tipo === 'VENCIMIENTO_MENOS_N' || tipo === 'VENCIMIENTO_MAS_N';
}

export function defaultPoliticaForm(): PoliticaVencimientoForm {
  return {
    aplicar: 'false',
    nombre: '',
    descripcion: '',
    tipo: 'DIA_PERIODO',
    ajusteDiaNoHabil: 'DIA_HABIL_SIGUIENTE',
    diaPeriodo: '15',
    diaSemana: '1',
    ocurrencia: '1',
    dias: '0',
  };
}

export interface PoliticaVencimientoForm {
  aplicar: string;
  nombre: string;
  descripcion: string;
  tipo: PoliticaVencimientoTipo;
  ajusteDiaNoHabil: AjusteDiaNoHabil;
  diaPeriodo: string;
  diaSemana: string;
  ocurrencia: string;
  dias: string;
}

export function dtoToForm(dto: CategoriaActividadPoliticaVencimientoDTO): PoliticaVencimientoForm {
  const tipo = dto.tipo ?? 'DIA_PERIODO';
  let dias = '0';
  if (dto.dias != null) {
    dias = String(dto.dias);
  } else if (isTipoLegacyRelativo(tipo)) {
    dias = '3';
  }

  return {
    aplicar: String(dto.aplicar ?? false),
    nombre: dto.nombre ?? '',
    descripcion: dto.descripcion ?? '',
    tipo,
    ajusteDiaNoHabil: dto.ajusteDiaNoHabil ?? 'DIA_HABIL_SIGUIENTE',
    diaPeriodo: dto.diaPeriodo != null ? String(dto.diaPeriodo) : '15',
    diaSemana: dto.diaSemana != null ? String(dto.diaSemana) : '1',
    ocurrencia: dto.ocurrencia != null ? String(dto.ocurrencia) : '1',
    dias,
  };
}

export function formToDto(form: PoliticaVencimientoForm): CategoriaActividadPoliticaVencimientoDTO {
  const tipo = form.tipo;
  const base: CategoriaActividadPoliticaVencimientoDTO = {
    aplicar: form.aplicar === 'true',
    nombre: form.nombre.trim(),
    descripcion: form.descripcion.trim() || null,
    tipo,
  };

  if (tipo === 'DIA_PERIODO') {
    return {
      ...base,
      diaPeriodo: Number(form.diaPeriodo),
      ajusteDiaNoHabil: form.ajusteDiaNoHabil,
    };
  }

  if (tipo === 'DIA_SEMANA_MES') {
    return {
      ...base,
      diaSemana: Number(form.diaSemana),
      ocurrencia: Number(form.ocurrencia),
      ajusteDiaNoHabil: form.ajusteDiaNoHabil,
    };
  }

  if (tipo === 'DIA_VENCIMIENTO_BASE') {
    const dias = form.dias.trim() === '' ? 0 : Number(form.dias);
    return {
      ...base,
      dias,
    };
  }

  return {
    ...base,
    dias: Number(form.dias),
  };
}

export function validatePoliticaForm(form: PoliticaVencimientoForm): Partial<Record<keyof PoliticaVencimientoForm, string>> {
  const errs: Partial<Record<keyof PoliticaVencimientoForm, string>> = {};

  if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
  if (!form.tipo) errs.tipo = 'El tipo de regla es requerido';

  if (form.tipo === 'DIA_PERIODO') {
    const dia = Number(form.diaPeriodo);
    if (Number.isNaN(dia) || dia < 1 || dia > 31) errs.diaPeriodo = 'Debe ser un día entre 1 y 31';
    if (!form.ajusteDiaNoHabil) errs.ajusteDiaNoHabil = 'El ajuste es requerido';
  }

  if (form.tipo === 'DIA_SEMANA_MES') {
    const diaSemana = Number(form.diaSemana);
    const ocurrencia = Number(form.ocurrencia);
    if (Number.isNaN(diaSemana) || diaSemana < 1 || diaSemana > 7) errs.diaSemana = 'Seleccione un día de la semana';
    if (Number.isNaN(ocurrencia) || ocurrencia < 1 || ocurrencia > 5) errs.ocurrencia = 'Seleccione una ocurrencia';
    if (!form.ajusteDiaNoHabil) errs.ajusteDiaNoHabil = 'El ajuste es requerido';
  }

  if (form.tipo === 'DIA_VENCIMIENTO_BASE') {
    const dias = Number(form.dias);
    if (form.dias.trim() !== '' && (Number.isNaN(dias) || !Number.isInteger(dias))) {
      errs.dias = 'Debe ser un número entero (0, negativo o positivo)';
    }
  }

  if (isTipoLegacyRelativo(form.tipo)) {
    const dias = Number(form.dias);
    if (Number.isNaN(dias) || dias <= 0 || !Number.isInteger(dias)) {
      errs.dias = 'Debe ser un entero mayor a 0';
    }
  }

  return errs;
}

export function getDiasVencimientoBaseHint(dias: string): string | undefined {
  const trimmed = dias.trim();
  if (trimmed === '' || Number.isNaN(Number(trimmed))) return undefined;

  const n = Number(trimmed);
  if (n === 0) return 'Vence exactamente el día de vencimiento SAT (ya es día hábil).';
  const abs = Math.abs(n);
  const unidad = abs === 1 ? 'día hábil' : 'días hábiles';
  if (n < 0) return `Vence ${abs} ${unidad} antes del vencimiento SAT.`;
  return `Vence ${abs} ${unidad} después del vencimiento SAT.`;
}
